"use client"

import * as React from "react"
import ContentPageLayout from "../../components/ContentPageLayout"
import CommunityContentLayout from "../../components/CommunityContentLayout"
import { StaticImage } from "gatsby-plugin-image"
import config from "../../config"
import firebase from "firebase"
import { firebaseInit } from "../../config"
import { navigate } from "gatsby"
import ContentCard from "../../components/content/contentCard.js"
import AvatarEdit from "../../components/social/AvatarEdit"

// Devuelve la fecha de hoy en zona UTC-6 con formato “YYYY-MM-DD”
function getUTC6Today() {
  // Timestamp UTC actual menos 6 horas
  const tsUtc6 = Date.now() - 6 * 60 * 60 * 1000
  const d = new Date(tsUtc6)
  const yyyy = d.getUTCFullYear()
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0")
  const dd = String(d.getUTCDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const UserHistoryPage = ({ location }) => {
  // --- Datos de usuario ---
  const [formValues, setFormValues] = React.useState({
    id: "",
    nombre: "",
    avatar: "",
  })
  const [isPremium, setIsPremium] = React.useState(false)
  const [category, setCategory] = React.useState("")

  // --- Token y fechas filtro ---
  const [dateFrom, setDateFrom] = React.useState("")
  const [dateTo, setDateTo] = React.useState("")
  const [userToken, setUserToken] = React.useState("")

  // --- Historial ---
  const [history, setHistory] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState("")

  // Inicializa dateFrom y dateTo con la fecha de hoy en UTC-6
  React.useEffect(() => {
    const today = getUTC6Today()
    setDateFrom(today)
    setDateTo(today)
  }, [])

  // 1) Inicializar Firebase y cargar perfil + token
  React.useEffect(() => {
    firebaseInit(firebase)
    const unregister = firebase.auth().onAuthStateChanged(async user => {
      if (!user) {
        navigate("/")
        return
      }
      const token = await user.getIdToken(true)
      setUserToken(token)

      fetch(`${config.serverUri}/users?email=${user.email}`, {
        headers: { "x-sc-signature": token },
      })
        .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
        .then(data => {
          const acct = data[0]
          setFormValues({
            id: acct.id,
            nombre: acct.nombre,
            avatar: acct.avatar?.startsWith("http")
              ? acct.avatar
              : acct.avatar
              ? `${config.serverUri}${acct.avatar}`
              : "",
          })
          setIsPremium(acct.suscription === "premium")
          setCategory(
            acct.predominantCategory === "ninguna"
              ? "Usuario Shift"
              : acct.predominantCategory
          )
        })
        .catch(() => navigate("/"))
    })
    return () => unregister()
  }, [])

  // 2) Recargar historial al cambiar filtro o usuario
  React.useEffect(() => {
    if (!formValues.id || !userToken) return

    setLoading(true)
    setError("")

    let endpoint = `${config.serverUri}/reportb2b-cont-visto/historial?userId=${formValues.id}`
    if (dateFrom) endpoint += `&dateCompleted_gte=${dateFrom}`
    if (dateTo) endpoint += `&dateCompleted_lte=${dateTo}`

    fetch(endpoint, {
      headers: { "x-sc-signature": userToken },
    })
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then(data => {
        setHistory(data)
        setLoading(false)
      })
      .catch(err => {
        console.error("Error al cargar historial:", err)
        setError("No se pudo cargar el historial")
        setLoading(false)
      })
  }, [formValues.id, userToken, dateFrom, dateTo])

  return (
    <>
      <ContentPageLayout location={location}>
        {/* Banner */}
        <div
          className="flex flex-col relative"
          style={{ height: 240, width: "100%" }}
        >
          <StaticImage
            alt="banner comunidad"
            src="../../images/banner_comunidad.png"
            objectFit="cover"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div className="absolute inset-x-0 top-24 text-center">
            {formValues.avatar && (
              <img loading="lazy" decoding="async"
                src={formValues.avatar}
                alt="Avatar"
                className="w-20 h-20 rounded-full mx-auto mb-2 object-cover"
              />
            )}
            <h3 className="text-white text-xl">{formValues.nombre}</h3>
            <h2 className="text-white">{category}</h2>
          </div>
        </div>

        <CommunityContentLayout
          avatar={formValues.avatar}
          isPremium={isPremium}
        >
          <div className="font-sans py-6">
            <h2 className="mb-4">Historial del usuario</h2>

            {/* Filtros de fecha */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="from"
                  className="block text-black text-sm font-medium mb-1"
                >
                  Desde
                </label>
                <input
                  type="date"
                  id="from"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="block w-full bg-transparent border-b border-gray-300 py-1 text-gray-700 focus:outline-none focus:border-gray-500"
                />
              </div>
              <div>
                <label
                  htmlFor="to"
                  className="block text-black text-sm font-medium mb-1"
                >
                  Hasta
                </label>
                <input
                  type="date"
                  id="to"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="block w-full bg-transparent border-b border-gray-300 py-1 text-gray-700 focus:outline-none focus:border-gray-500"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">Cargando historial...</div>
            ) : error ? (
              <div className="text-center text-red-500 py-8">{error}</div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                No hay registros en el período seleccionado.
              </div>
            ) : (
              <div className="flex flex-wrap justify-between my-8">
                {history.map(entry => (
                  <div key={entry.id} className="w-full sm:w-1/2 lg:w-1/3 p-2">
                    <ContentCard
                      card={entry.card}
                      userHasSuscription={isPremium ? "premium" : "free"}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </CommunityContentLayout>
      </ContentPageLayout>
    </>
  )
}

export default UserHistoryPage
